'use client'

import { useState } from 'react'

type Question = {
  id: string
  question: string
  options: { label: string; value: string }[]
}

type Recommendation = {
  primary: string
  secondary?: string
  monthlyCost: string
  reason: string
  link?: string
}

const questions: Question[] = [
  {
    id: 'experience',
    question: 'What\'s your coding experience level?',
    options: [
      { label: 'Beginner (< 1 year)', value: 'beginner' },
      { label: 'Intermediate (1-3 years)', value: 'intermediate' },
      { label: 'Senior (3+ years, terminal-comfortable)', value: 'senior' },
      { label: 'Non-technical (building with AI only)', value: 'non-technical' },
    ],
  },
  {
    id: 'workflow',
    question: 'What do you spend most of your coding time doing?',
    options: [
      { label: 'Editing existing code (refactors, bug fixes, small changes)', value: 'editing' },
      { label: 'Shipping complete features (multi-file, end-to-end)', value: 'shipping' },
      { label: 'Delegating tasks (want to hand off and review later)', value: 'delegating' },
      { label: 'Learning / exploring (building to understand)', value: 'learning' },
    ],
  },
  {
    id: 'team',
    question: 'How do you work?',
    options: [
      { label: 'Solo developer or founder', value: 'solo' },
      { label: 'Small team (2-15 people)', value: 'small-team' },
      { label: 'Larger team (15+ engineers)', value: 'large-team' },
    ],
  },
  {
    id: 'budget',
    question: 'What\'s your monthly budget for AI coding tools?',
    options: [
      { label: 'Free / as cheap as possible', value: 'free' },
      { label: '$20-50/mo', value: 'moderate' },
      { label: '$100-250/mo (serious investment)', value: 'high' },
      { label: 'Whatever ships fastest (cost is not the constraint)', value: 'unlimited' },
    ],
  },
]

function getRecommendation(answers: Record<string, string>): Recommendation {
  const { experience, workflow, team, budget } = answers

  if (experience === 'non-technical' || experience === 'beginner') {
    if (budget === 'free') {
      return {
        primary: 'Cursor (Free tier)',
        monthlyCost: '$0-20/mo',
        reason: 'Cursor\'s VS Code foundation and Cmd+K natural-language editing is the gentlest on-ramp. Start free, upgrade to Pro ($20/mo) when you hit limits.',
        link: '/blog/vibe-coding-non-engineers',
      }
    }
    return {
      primary: 'Cursor Pro',
      monthlyCost: '$20/mo',
      reason: 'Best beginner experience in the category. Familiar editor feel, intuitive Cmd+K editing, and Tab completion that teaches you patterns as you code. Skip everything else until you\'re comfortable.',
      link: '/courses/cursor-courses',
    }
  }

  if (workflow === 'delegating') {
    if (budget === 'free') {
      return {
        primary: 'Google Antigravity',
        monthlyCost: '$0 (free preview)',
        reason: 'Antigravity\'s parallel agents + browser verification let you delegate multi-step tasks and review video proof. Free during preview. Best delegation experience at zero cost.',
        link: '/blog/google-antigravity-complete-guide',
      }
    }
    if (budget === 'unlimited' || budget === 'high') {
      return {
        primary: 'Antigravity + Devin',
        secondary: 'Antigravity for real-time delegation, Devin for overnight async PRs',
        monthlyCost: '$500/mo (Devin) + $0 (Antigravity preview)',
        reason: 'Maximum delegation stack. Antigravity handles tasks where you want video-verified results in minutes. Devin handles well-scoped tasks you want as PRs by morning. Different shapes of delegation.',
        link: '/blog/google-antigravity-complete-guide',
      }
    }
    return {
      primary: 'Google Antigravity',
      monthlyCost: '$0 (free preview)',
      reason: 'Best delegation experience available. Dispatch parallel agents, get video proof of working features. Free during preview with personal Gmail.',
      link: '/blog/google-antigravity-complete-guide',
    }
  }

  if (workflow === 'shipping' && (experience === 'senior' || experience === 'intermediate')) {
    if (budget === 'high' || budget === 'unlimited') {
      return {
        primary: 'Claude Code Max + Cursor Pro',
        secondary: 'Claude Code for feature shipping, Cursor for daily editing',
        monthlyCost: '$220/mo combined',
        reason: 'The highest-productivity stack in our 40-engineer survey. Cursor handles Tab + inline edits. Claude Code handles multi-file features with ~70% task completion. Engineers using this combo report 2.5x more features shipped per week.',
        link: '/courses/claude-code-course',
      }
    }
    if (budget === 'moderate') {
      return {
        primary: 'Cursor Pro',
        secondary: 'Add Claude Code API (pay-per-token) for bigger tasks',
        monthlyCost: '$20-60/mo depending on Claude usage',
        reason: 'Cursor Pro covers 80% of daily work. Use Claude Code on API pricing (not Max) for occasional larger features. That keeps costs controlled while giving access to the best agent when needed.',
        link: '/courses/cursor-courses',
      }
    }
    return {
      primary: 'Aider (free, BYO API keys)',
      secondary: 'Or Cline as a VS Code extension alternative',
      monthlyCost: '$5-30/mo in API costs only',
      reason: 'Aider gives you Claude Code-quality workflows with full model choice and zero subscription. You pay only API tokens. Active OSS community, excellent multi-file editing.',
    }
  }

  if (workflow === 'editing') {
    if (team === 'large-team') {
      return {
        primary: 'Cursor Business',
        secondary: 'Or Windsurf Teams if SSO/audit matters more than Tab speed',
        monthlyCost: '$40/seat (Cursor) or $30/seat (Windsurf)',
        reason: 'For editing-heavy team workflows, Cursor Business has the best Tab completion and enterprise features. Windsurf is cheaper with stronger admin controls if your procurement team cares about SSO/audit.',
      }
    }
    if (team === 'small-team') {
      return {
        primary: 'Windsurf Pro',
        secondary: 'Or Cursor Pro if Tab completion speed matters more than price',
        monthlyCost: '$15-20/mo per seat',
        reason: 'Windsurf at $15/mo is the best value for teams focused on daily editing. Cursor Pro at $20/mo has noticeably faster Tab completion. Pick on which $5/mo feels like.',
      }
    }
    if (budget === 'free') {
      return {
        primary: 'Cline (free VS Code extension)',
        monthlyCost: '$5-15/mo in API costs',
        reason: 'Cline lives inside vanilla VS Code, so no editor migration is needed. BYO API keys, pick your model. Good agent capabilities without any subscription.',
      }
    }
    return {
      primary: 'Cursor Pro',
      monthlyCost: '$20/mo',
      reason: 'Best Tab completion in the category. Period. If editing speed is your priority, nothing else comes close. The fastest autocomplete makes every keystroke 30% more productive.',
      link: '/courses/cursor-courses',
    }
  }

  if (workflow === 'learning') {
    return {
      primary: 'Cursor Pro',
      secondary: 'Graduate to Claude Code once terminal-comfortable',
      monthlyCost: '$20/mo',
      reason: 'Cursor is the best learning environment. It shows you patterns as you code, the Cmd+K interface teaches you to communicate intent clearly, and the VS Code ecosystem has every tutorial. Once you\'re comfortable in the terminal, add Claude Code for 10x leverage.',
      link: '/courses/coding-fundamental-course',
    }
  }

  if (team === 'large-team') {
    return {
      primary: 'Cody (Sourcegraph) + Cursor Business',
      monthlyCost: '$49+/seat combined',
      reason: 'Large codebases need code intelligence (Cody) plus fast editing (Cursor). Cody understands your monorepo context across millions of lines. Cursor handles the daily editing workflow.',
    }
  }

  return {
    primary: 'Cursor Pro + Claude Code Max',
    secondary: 'The default "best stack" recommendation',
    monthlyCost: '$220/mo',
    reason: 'Our survey data says this is the highest-productivity combination. Cursor for editing, Claude Code for shipping. 25% of working engineers already use this stack.',
    link: '/courses/claude-code-course',
  }
}

export default function CodingAgentQuiz() {
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [showResult, setShowResult] = useState(false)

  const handleAnswer = (questionId: string, value: string) => {
    const newAnswers = { ...answers, [questionId]: value }
    setAnswers(newAnswers)

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      setShowResult(true)
    }
  }

  const reset = () => {
    setCurrentStep(0)
    setAnswers({})
    setShowResult(false)
  }

  const recommendation = showResult ? getRecommendation(answers) : null

  if (showResult && recommendation) {
    return (
      <div className="my-10 rounded-xl border border-green-500/30 bg-gradient-to-br from-green-500/5 to-emerald-500/5 p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-green-400 text-lg">✓</span>
          <h3 className="text-xl font-bold text-white m-0">Your Recommended Stack</h3>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
            <span className="text-2xl font-bold text-white">{recommendation.primary}</span>
            <span className="text-sm text-green-400 font-medium">{recommendation.monthlyCost}</span>
          </div>

          {recommendation.secondary && (
            <p className="text-sm text-gray-400 italic m-0">{recommendation.secondary}</p>
          )}

          <p className="text-gray-300 leading-relaxed m-0">{recommendation.reason}</p>

          {recommendation.link && (
            <a
              href={recommendation.link}
              className="inline-flex items-center gap-2 mt-2 text-sm font-medium text-green-400 hover:text-green-300 transition-colors no-underline"
            >
              Learn more →
            </a>
          )}
        </div>

        <button
          onClick={reset}
          className="mt-6 text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer bg-transparent border-0 p-0 underline"
        >
          Retake quiz with different answers
        </button>
      </div>
    )
  }

  const question = questions[currentStep]

  return (
    <div className="my-10 rounded-xl border border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-purple-500/5 p-6 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-white m-0">Which AI Coding Agent Should You Use?</h3>
        <span className="text-xs text-gray-500 font-mono">
          {currentStep + 1}/{questions.length}
        </span>
      </div>

      <div className="mb-2 h-1 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
          style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
        />
      </div>

      <p className="text-gray-300 text-lg mb-5 mt-5">{question.question}</p>

      <div className="flex flex-col gap-3">
        {question.options.map((option) => (
          <button
            key={option.value}
            onClick={() => handleAnswer(question.id, option.value)}
            className="w-full text-left px-4 py-3 rounded-lg border border-gray-700 bg-gray-900/50 text-gray-200 hover:border-blue-500/50 hover:bg-blue-500/5 hover:text-white transition-all cursor-pointer text-sm sm:text-base"
          >
            {option.label}
          </button>
        ))}
      </div>

      {currentStep > 0 && (
        <button
          onClick={() => setCurrentStep(currentStep - 1)}
          className="mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer bg-transparent border-0 p-0"
        >
          ← Back
        </button>
      )}
    </div>
  )
}
